import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { HiOutlineMail, HiOutlineUser } from "react-icons/hi";
import { RiMessage2Line, RiChatHeartLine } from "react-icons/ri";
import { FiSend } from "react-icons/fi";
import contactImg from "../assets/contactus.jpg"; // Your image path

const Contact = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden py-20 px-6 bg-[#FFF0F3]">
      {/* Background Decorative Elements (Matching the Landscape Theme) */}
      <div className="absolute inset-0 z-0">
        <motion.div 
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-rose-200 rounded-full blur-[100px]"
        />
        <div className="absolute bottom-0 w-full opacity-30">
          <svg viewBox="0 0 1440 320" className="w-full h-auto">
            <path fill="#F43F5E" d="M0,160L48,176C96,192,192,224,288,213.3C384,203,480,149,576,144C672,139,768,181,864,181.3C960,181,1056,139,1152,122.7C1248,107,1344,117,1392,122.7L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
      </div>

      {/* Main Glassmorphic Card */}
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-6xl bg-white/20 backdrop-blur-[30px] border border-white/40 rounded-[3rem] shadow-2xl overflow-hidden flex flex-col md:flex-row"
      >
        
        {/* Left Side: Brand & Image Section */}
        <div className="md:w-5/12 bg-rose-500/10 p-12 flex flex-col justify-between border-r border-white/20">
          <div>
            <div className="flex items-center gap-2 text-rose-600 font-bold mb-6 tracking-tighter text-xl">
              <RiChatHeartLine size={28} />
              <span>EVEYA CARE</span>
            </div>
            <h1 className="text-4xl font-black text-rose-950 leading-tight mb-4 uppercase tracking-tighter">
              We're here <br /> for you.
            </h1>
            <p className="text-rose-900/60 font-medium">
              Have questions about your subscription, products, or wellness? Our care team is listening.
            </p>
          </div>

          <motion.div 
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-8 relative"
          >
            <div className="absolute inset-0 bg-rose-200 blur-2xl rounded-full opacity-30 animate-pulse"></div>
            <img
              src={contactImg}
              alt="Care Team"
              className="relative w-full h-72 object-cover rounded-[2rem] shadow-lg grayscale-[20%] hover:grayscale-0 transition-all duration-500"
            />
          </motion.div>
        </div>

        {/* Right Side: Underline Style Form */}
        <div className="md:w-7/12 p-12 md:p-16">
          <form className="space-y-10">
            {/* Name Input */}
            <div className="relative border-b-2 border-rose-900/10 focus-within:border-rose-500 transition-all py-2">
              <label className="text-[10px] font-bold text-rose-900/40 uppercase tracking-widest block mb-1">Full Name</label>
              <input 
                type="text" 
                placeholder="How should we call you?" 
                className="w-full bg-transparent outline-none text-rose-950 placeholder:text-rose-900/20 text-lg font-medium"
              />
              <HiOutlineUser className="absolute right-0 bottom-3 text-rose-900/30" size={22} />
            </div>

            {/* Email Input */}
            <div className="relative border-b-2 border-rose-900/10 focus-within:border-rose-500 transition-all py-2">
              <label className="text-[10px] font-bold text-rose-900/40 uppercase tracking-widest block mb-1">Email Address</label>
              <input 
                type="email" 
                placeholder="email@example.com" 
                className="w-full bg-transparent outline-none text-rose-950 placeholder:text-rose-900/20 text-lg font-medium"
              />
              <HiOutlineMail className="absolute right-0 bottom-3 text-rose-900/30" size={22} />
            </div>

            {/* Message Input */}
            <div className="relative border-b-2 border-rose-900/10 focus-within:border-rose-500 transition-all py-2">
              <label className="text-[10px] font-bold text-rose-900/40 uppercase tracking-widest block mb-1">Your Message</label>
              <textarea 
                rows="3"
                placeholder="Tell us what's on your mind..." 
                className="w-full bg-transparent outline-none text-rose-950 placeholder:text-rose-900/20 text-lg font-medium resize-none"
              ></textarea>
              <RiMessage2Line className="absolute right-0 bottom-3 text-rose-900/30" size={22} />
            </div>

            {/* Submit Button */}
            <motion.button
              whileHover={{ scale: 1.02, backgroundColor: "#E11D48" }}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-rose-500 text-white py-5 rounded-2xl font-bold text-lg shadow-xl shadow-rose-200 flex items-center justify-center gap-3 transition-colors"
            >
              Send Message <FiSend size={20} />
            </motion.button>
          </form>

          {/* Contact Details Footer */}
          <div className="mt-12 flex flex-wrap gap-8">
            <div>
              <p className="text-[10px] font-bold text-rose-900/40 uppercase tracking-widest">Email Support</p>
              <p className="text-sm font-bold text-rose-900">hello@EVEYA.com</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-rose-900/40 uppercase tracking-widest">Talk to us</p>
              <p className="text-sm font-bold text-rose-900">+1 (234) 567-FLOW</p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Contact;